import type { FeeConditionNode } from "../fleet-settings/types";
import type { AddonPhoto } from "./photos/types";

/**
 * What an addon IS. Declared by the operator, never inferred from its name.
 * `null` means nobody has classified it yet — distinct from an explicit
 * "other".
 */
export type AddonCategory =
  | "equipment"
  | "service"
  | "supplies"
  | "package"
  | "other";

export const ADDON_CATEGORIES: Array<{ value: AddonCategory; label: string }> =
  [
    { value: "equipment", label: "Equipment" },
    { value: "service", label: "Service" },
    { value: "supplies", label: "Supplies" },
    { value: "package", label: "Package" },
    { value: "other", label: "Other" },
  ];

/**
 * How many of an add-on there are in each region it is offered in.
 *
 * A region that is absent from the map has NO limit there — distinct from a
 * region mapped to 0, which is sold out. Collapsing the two would put an
 * out-of-stock add-on back on sale.
 */
export type AddonStockByRegion = Record<string, number>;

/**
 * How an add-on picks the vehicles that offer it: every vehicle, the ones a
 * rule (RV class / vehicle type / category) matches, or a hand-made list.
 */
export type AddonVehicleScope = "all" | "filters" | "specific";

/**
 * Why a vehicle does or does not offer an add-on. `extra` is an include on a
 * vehicle the rule does not match; `skipped` is an exclude on one it does.
 */
export type AddonScopeReason =
  | "all"
  | "filters"
  | "specific"
  | "extra"
  | "skipped"
  | "not_offered";

/** The scope fields an add-on carries and accepts on create and update. */
export type AddonScopeFields = {
  vehicle_scope: AddonVehicleScope;
  /** RV class catalog ids. Only read while `vehicle_scope` is "filters". */
  rv_class_ids: number[];
  /** Vehicle type catalog ids. Only read while `vehicle_scope` is "filters". */
  vehicle_type_ids: number[];
  /** Category public ids. Only read while `vehicle_scope` is "filters". */
  category_ids: string[];
  /** Vehicle public ids offered on top of the rule (or the whole list). */
  include_vehicle_ids: string[];
  /** Vehicle public ids the rule matches but that are skipped. */
  exclude_vehicle_ids: string[];
};

export type Addon = AddonScopeFields & {
  /** How many vehicles offer this add-on right now. Read only. */
  offered_vehicle_count: number;
  /**
   * Vehicles the vehicle rule offers but the add-on's regions hide, so
   * renters never get it on them. Absent from an older server: read it as 0.
   * `offered_vehicle_count` already leaves these out.
   */
  outside_region_vehicle_count?: number;
  /**
   * The operating regions this add-on is offered in. `null` or absent means
   * every region, which is every add-on that exists today.
   *
   * Unlike a rate plan's scope, this is availability rather than a
   * fallback: an add-on limited to Phoenix AZ is simply not offered on a
   * Salt Lake City UT booking.
   */
  region_ids?: string[] | null;
  /**
   * Per-region stock, and only meaningful while `region_ids` names regions:
   * a fleet-wide add-on has no per-region count. `null` or absent is no
   * limit anywhere.
   */
  stock_by_region?: AddonStockByRegion | null;
  id: string;
  name: string;
  description: string | null;
  price: string; // API returns price as string (e.g., "100.00")
  unit: "one_time" | "per_day" | "per_hour" | "per_mile";
  /** Bill usage past an included allowance. Only valid for a per_hour or
   * per_mile unit, and requires both fields below. */
  metered: boolean;
  /** Units included per rental day. Null when metering is off; 0 is a real
   * policy meaning every unit is billable. */
  included_quantity_per_day: string | number | null;
  /** Charge per unit past the allowance. Distinct from `price`, which is what
   * the add-on itself costs. */
  overage_price: string | number | null;
  category: AddonCategory | null;
  required: boolean;
  /**
   * When the add-on applies, as the same condition tree a fee carries. Absent,
   * `null` or `{}` mean none (an older server omits the key). An add-on WITH
   * conditions is automatic: added and locked when the rental matches, not
   * offered otherwise, and `required` plays no part.
   */
  conditions?: FeeConditionNode | Record<string, never> | null;
  active: boolean;
  /**
   * Staff can charge a hidden add-on, but the guest-facing endpoints never
   * list it, so renters are not offered it in checkout.
   */
  hidden: boolean;
  sort_order: number;
  tic_code: number | null;
  /**
   * Whether the add-on is part of the taxable amount on quotes, bookings and
   * rentals. The TIC code never makes an add-on exempt; this does. Absent from
   * an older server: read it as taxable.
   */
  taxable?: boolean;
  /**
   * Ordered by position. Present on every payload that renders an add-on,
   * including the vehicle and category lists checkout reads, so a storefront
   * never has to fetch photos add-on by add-on.
   */
  photos: AddonPhoto[];
  inserted_at: string;
  updated_at: string;
};

export type AssignAddonRequest = {
  addon_id: string;
};

export type CreateAddonRequest = Partial<AddonScopeFields> & {
  /**
   * The regions to offer this add-on in; `null` for every region. LEFT OUT
   * entirely by a fleet with one region or none, so its write comes out
   * identical to the one it sent before regions existed.
   */
  region_ids?: string[] | null;
  /** Per-region stock; `null` for no limit anywhere. */
  stock_by_region?: AddonStockByRegion | null;
  /**
   * The condition tree; `null` clears it. Left out unless there are
   * conditions to set or existing ones to clear.
   */
  conditions?: FeeConditionNode | null;
  name: string;
  price: number;
  /** Required on creation: the API rejects an addon that declares no category. */
  category: AddonCategory;
  description?: string;
  unit?: "one_time" | "per_day" | "per_hour" | "per_mile";
  metered?: boolean;
  included_quantity_per_day?: number | null;
  overage_price?: number | null;
  required?: boolean;
  active?: boolean;
  hidden?: boolean;
  sort_order?: number;
  tic_code?: number;
  /**
   * Left out unless the person changed it: the server defaults to taxable, and
   * an older server does not know the key.
   */
  taxable?: boolean;
};

export type UpdateAddonRequest = Partial<CreateAddonRequest>;

export type AddonFilters = {
  page?: number;
  per_page?: number;
  active?: boolean;
};

/**
 * One row of `GET /vehicles/:vehicle_id/addon-scope`: every add-on of the
 * fleet, with whether this vehicle offers it and why. `offered` is false for
 * an inactive add-on whatever the rule says.
 */
export type AddonScopeItem = Addon & {
  reason: AddonScopeReason;
  offered: boolean;
  /**
   * Whether the add-on's regions include this vehicle's region. `offered` and
   * `reason` read the vehicle rule only: renters get the add-on when
   * `offered && in_region`. Absent from an older server: read it as true.
   */
  in_region?: boolean;
};

/** One row of `GET /vehicles/:vehicle_id/addons`. */
export type VehicleAddon = Addon & {
  /** Whether the add-on's regions include this vehicle's region. */
  in_region?: boolean;
};

/** What the add-on list says about itself (an older server may omit it). */
export type AddonListMeta = {
  /** Every add-on of the fleet, not only the ones in `data`. */
  total?: number;
  page?: number;
  per_page?: number;
  pages?: number;
};

/** The most add-ons one bulk-conditions call accepts (the server's cap). */
export const ADDONS_BULK_CONDITIONS_MAX = 100;

export type BulkAddonConditionsRequest = {
  /** Add-on public ids, at most `ADDONS_BULK_CONDITIONS_MAX`. */
  addon_ids: string[];
  /** The tree to set; `null` clears. Replaces the existing conditions. */
  conditions: FeeConditionNode | null;
};

export type BulkAddonConditionsResponse = {
  /** The updated add-ons, shaped like the add-on list. */
  data: Addon[];
  meta: { updated: number };
};
